import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ArrowRightAltRoundedIcon from "@mui/icons-material/ArrowRightAltRounded";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import { Stack, TableCell, TableRow, Typography } from "@mui/material";
import type { ReactElement } from "react";
import StatusChip from "@entrilia/shared/components/StatusChip";
import type { MemberChangeAction } from "../../../../../api/types/workflowTypes";
import PortalRoleChip from "../../../../common/PortalRoleChip";
import type { MemberCategoryChangeView, MemberFundChangeGroup } from "./membersChangeRequestHelper";

interface Props {
  group: MemberFundChangeGroup;
  action: MemberChangeAction;
}

const categoryChipIcons: Record<MemberCategoryChangeView["kind"], ReactElement> = {
  Added: <AddRoundedIcon />,
  Removed: <RemoveRoundedIcon />,
};

interface CategoryChipProps {
  category: MemberCategoryChangeView;
}

const CategoryChip = ({ category }: CategoryChipProps) => (
  <StatusChip
    icon={categoryChipIcons[category.kind]}
    label={category.name}
    color={(t) => (category.kind === "Removed" ? t.palette.error.main : t.palette.success.main)}
  />
);

const FundChangeRow = ({ group, action }: Props) => {
  const { change } = group;

  return (
    <TableRow>
      <TableCell sx={{ verticalAlign: "top" }}>
        <Stack>
          {group.fundNames.map((fundName) => (
            <Typography key={fundName}>{fundName}</Typography>
          ))}
        </Stack>
      </TableCell>
      <TableCell sx={{ verticalAlign: "top" }}>
        <Stack spacing={1.5}>
          {change.oldRole !== change.newRole && (
            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
              <Typography variant="caption" color="textSecondary" sx={{ minWidth: (t) => t.spacing(13) }}>
                Role:
              </Typography>
              {action === "UpdateExisting" ? (
                <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                  <PortalRoleChip role={change.oldRole} />
                  <ArrowRightAltRoundedIcon sx={{ color: "text.secondary" }} />
                  <PortalRoleChip role={change.newRole} />
                </Stack>
              ) : (
                <PortalRoleChip role={action === "Delete" ? change.oldRole : change.newRole} />
              )}
            </Stack>
          )}
          {change.categoryChanges.length > 0 && (
            <Stack direction="row" spacing={1} sx={{ alignItems: "flex-start" }}>
              <Typography variant="caption" color="textSecondary" sx={{ minWidth: (t) => t.spacing(13) }}>
                Documents:
              </Typography>
              <Stack direction="row" sx={{ flexWrap: "wrap", gap: 1 }}>
                {change.categoryChanges.map((category) => (
                  <CategoryChip key={`${category.kind}-${category.name}`} category={category} />
                ))}
              </Stack>
            </Stack>
          )}
        </Stack>
      </TableCell>
    </TableRow>
  );
};

export default FundChangeRow;
